/**
 * Admin read-side schemas for `audit_log` (Story 3.17).
 *
 * Source of truth: `apps/api/src/db/schema.ts` §4.12 `auditLog` + the
 * `auditAction` pgEnum (close to 80 known actions across all epics).
 *
 * **Read-only surface (Story 3.17 §AC #4):** no POST/PATCH/DELETE schemas.
 * The route returns 405 for mutating verbs.
 *
 * **Keyset pagination (§AC #1 + §Decision 1):** `nextCursor` is a base64-
 * encoded string of `${createdAt.toISOString()}__${id}`. The client passes
 * it back as-is in `?cursor=...`; the route decodes it. Cursor is opaque
 * to the UI.
 */
import { Type } from '@sinclair/typebox';
/**
 * `AuditLogEntry` — public projection returned by GET /admin/audit-log.
 *
 * `actorEmail` is enriched server-side via a join with `users` (LRU cache,
 * TTL 60s — see `audit-log.repo.ts`). `null` when the action was emitted
 * by a worker or system process (no `actor_user_id`).
 *
 * `payload` is the raw jsonb minus sensitive keys (`token`, `password`,
 * `secret`, `apiKey`, `authorization`) — see `redactSensitive` in the repo.
 */
export const AuditLogEntry = Type.Object({
    id: Type.String({ format: 'uuid' }),
    action: Type.String({ minLength: 1 }),
    resourceType: Type.String({ minLength: 1 }),
    resourceId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    actorUserId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    actorEmail: Type.Union([Type.String(), Type.Null()]),
    correlationId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    payload: Type.Unknown(),
    createdAt: Type.String({ format: 'date-time' }),
});
/**
 * `AuditLogListResponse` — keyset-paginated response envelope.
 *
 * `nextCursor` is `null` when the last page is reached.
 */
export const AuditLogListResponse = Type.Object({
    items: Type.Array(AuditLogEntry),
    nextCursor: Type.Union([Type.String(), Type.Null()]),
});
/**
 * `AuditLogListQuery` — typed query-string parameters for the list endpoint.
 *
 * All fields optional. The route parses these manually because Fastify's
 * TypeBox query coercion is limited (e.g. `date-time` doesn't auto-coerce).
 */
export const AuditLogListQuery = Type.Object({
    action: Type.Optional(Type.String()),
    resourceType: Type.Optional(Type.String()),
    actorUserId: Type.Optional(Type.String({ format: 'uuid' })),
    correlationId: Type.Optional(Type.String({ format: 'uuid' })),
    from: Type.Optional(Type.String({ format: 'date-time' })),
    to: Type.Optional(Type.String({ format: 'date-time' })),
    cursor: Type.Optional(Type.String()),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
});
/**
 * Maximum number of items returned per page. Enforced server-side; the UI
 * uses 50 as the default for snappier first-paint.
 */
export const AUDIT_LOG_LIMIT_DEFAULT = 50;
export const AUDIT_LOG_LIMIT_MAX = 200;
/**
 * Sensitive key fragments that `redactSensitive` should mask. Match is
 * case-insensitive and substring-based, so `token`, `accessToken`,
 * `refresh_token`, `webhookSecret`, etc. all get masked.
 *
 * Exported so the frontend can show a tooltip explaining the `***REDACTED***`
 * placeholder.
 */
export const REDACTED_PLACEHOLDER = '***REDACTED***';
export const SENSITIVE_KEY_PATTERNS = [
    'token',
    'password',
    'secret',
    'apikey',
    'api_key',
    'authorization',
];
/**
 * Convenience presets for the date-range filter. The UI surfaces these as
 * preset buttons (1h / 24h / 7d / 30d); the route accepts them as `from` /
 * `to` ISO timestamps after client-side conversion.
 */
export const AUDIT_DATE_PRESETS = ['1h', '24h', '7d', '30d'];
//# sourceMappingURL=audit-log.js.map